// components/radiologyTechnicians/TechnicianCard.jsx
import React from 'react';
import {
    Card,
    CardContent,
    CardActions,
    Typography,
    Button,
    Box,
    Chip,
    IconButton,
    Tooltip,
    Stack,
    Divider
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Person as PersonIcon,
    Cake as AgeIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const TechnicianCard = ({
    technician,
    canEdit,
    canDelete,
    onEdit,
    onDelete
}) => {
    const { t } = useTranslation();
    const { isSuperAdmin } = useAuth()
    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                }
            }}
        >
            <CardContent sx={{ flexGrow: 1 }}>
                {/* Header */}
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                backgroundColor: 'primary.light',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <PersonIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" component="h2" fontWeight="medium">
                                {technician?.name || 'N/A'}
                            </Typography>
                            <Chip
                                label={technician?.isActive ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}
                                color={technician?.isActive ? 'success' : 'default'}
                                size="small"
                                sx={{ mt: 0.5 }}
                            />
                        </Box>
                    </Box>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                {/* Details */}
                <Stack spacing={1.5}>
                    {technician?.age && (
                        <Box display="flex" alignItems="center" gap={1}>
                            <AgeIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                                {t('radiologyTechnicians.age', 'Age')}:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                                {technician.age} {t('common.years', 'years')}
                            </Typography>
                        </Box>
                    )}

                    {technician?.createdAt && (
                        <Typography variant="caption" color="text.secondary">
                            {t('common.createdAt', 'Created')}: {new Date(technician.createdAt).toLocaleDateString()}
                        </Typography>
                    )}
                </Stack>

            </CardContent>

            {/* Actions */}
            {(canEdit || canDelete) && (
                <CardActions sx={{ justifyContent: 'flex-end', pt: 0, px: 2, pb: 2 }}>
                    <Stack direction="row" spacing={1}>
                        {canEdit && (
                            <Tooltip title={t('common.edit', 'Edit')}>
                                <Button
                                    size="small"
                                    startIcon={<EditIcon />}
                                    onClick={() => onEdit(technician)}
                                >
                                    {t('common.edit', 'Edit')}
                                </Button>
                            </Tooltip>
                        )}
                        {canDelete && (
                            <Tooltip title={t('common.delete', 'Delete')}>
                                <Button
                                    size="small"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={() => onDelete(technician)}
                                >
                                    {t('common.delete', 'Delete')}
                                </Button>
                            </Tooltip>
                        )}
                        {isSuperAdmin &&
                            <Link to={`/radiology-technicians/${technician._id}`}>
                                <Button size="small">
                                    {t('radiologyTechnicians.viewDetails', 'View Details')}

                                </Button>
                            </Link >
                        }

                    </Stack>
                </CardActions>
            )}
        </Card>
    );
};

export default TechnicianCard;